import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { getMainCategoryDisplayName, getCategoryEmoji, getCategoryDisplayName, getSubCategoryDisplayName, getSubCategoryStyles } from './categoryUtils';
import { useAppSettingsStore } from '../../stores/appSettingsStore';
import { cn } from '../../lib/utils';
import {
  borderPrimary,
  semiBoldText,
  primaryText,
  largeTextSize,
  smallTextSize,
  secondaryText,
  whiteBG,
  baseTextSize,
  mediumText,
} from '../../styling/stylingHelper';
import { DRINKS_CATEGORIES, FOOD_CATEGORIES, NON_FOOD_CATEGORIES, SUB_CATEGORIES_BY_MAIN_CATEGORY_AND_CATEGORY, MAIN_CATEGORIES } from './utils';

interface ProductCategoryModalProps {
  productName: string;
  currentCategory?: string;
  currentMainCategory?: string;
  currentSubCategories?: string[];
  onSelectCategory: (mainCategory: string, category: string, subCategories: string[]) => Promise<void>;
  onClose: () => void;
}

const ProductCategoryModal = ({
  productName,
  currentCategory,
  currentMainCategory,
  currentSubCategories = [],
  onSelectCategory,
  onClose,
}: ProductCategoryModalProps) => {
  useTranslation();
  const darkMode = useAppSettingsStore((s) => s.darkMode);
  // Determine initial mainCategory from currentMainCategory or infer from currentCategory
  const getInitialMainCategory = (): string => {
    if (currentMainCategory) return currentMainCategory;
    // Try to infer from currentCategory (backward compatibility)
    if (currentCategory) {
      if (DRINKS_CATEGORIES.includes(currentCategory as any)) return 'DRINKS';
      if (NON_FOOD_CATEGORIES.includes(currentCategory as any)) return 'NON_FOOD';
      return 'FOOD'; // Default to FOOD
    }
    return '';
  };

  const [selectedMainCategory, setSelectedMainCategory] = useState<string>(getInitialMainCategory());
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(currentCategory);
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>(currentSubCategories);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get available categories based on selected mainCategory
  const getAvailableCategories = (): string[] => {
    if (selectedMainCategory === 'FOOD') return [...FOOD_CATEGORIES];
    if (selectedMainCategory === 'DRINKS') return [...DRINKS_CATEGORIES];
    if (selectedMainCategory === 'NON_FOOD') return [...NON_FOOD_CATEGORIES];
    return [];
  };

  // Get available subcategories for selected category
  const getAvailableSubCategories = (): string[] => {
    if (!selectedCategory || !selectedMainCategory) return [];
    return SUB_CATEGORIES_BY_MAIN_CATEGORY_AND_CATEGORY[selectedMainCategory]?.[selectedCategory] || [];
  };

  // Handle mainCategory change - reset category and subcategories
  const handleMainCategoryChange = (mainCategory: string) => {
    setSelectedMainCategory(mainCategory);
    setSelectedCategory(undefined);
    setSelectedSubCategories([]);
  };

  // Handle category change - reset subcategories if they're not valid for new category
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    const availableSubs = selectedMainCategory 
      ? (SUB_CATEGORIES_BY_MAIN_CATEGORY_AND_CATEGORY[selectedMainCategory]?.[category] || [])
      : [];
    // Keep only subcategories that are still valid
    setSelectedSubCategories(prev => prev.filter(sub => availableSubs.includes(sub)));
  };

  // Handle subcategory toggle
  const handleSubCategoryToggle = (subCategory: string) => {
    setSelectedSubCategories(prev => {
      if (prev.includes(subCategory)) {
        return prev.filter(sub => sub !== subCategory);
      } else {
        return [...prev, subCategory];
      }
    });
  };

  const handleSubmit = async () => {
    if (!selectedMainCategory || !selectedCategory) return;

    setIsSubmitting(true);
    try {
      await onSelectCategory(selectedMainCategory, selectedCategory, selectedSubCategories);
      onClose();
    } catch (error) {
      console.error('Failed to update category:', error);
      alert('Failed to update category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableCategories = getAvailableCategories();
  const availableSubCategories = getAvailableSubCategories();

  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className={cn(
          "bg-white dark:bg-background-dark rounded-xl shadow-xl w-full max-w-lg border max-h-[90vh] flex flex-col",
          borderPrimary
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 flex-1 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-start mb-4 relative">
            <div>
              <h2 className={cn(semiBoldText, largeTextSize, primaryText, "mb-1")}>
                Change Product Category
              </h2>
              <p className={cn(smallTextSize, secondaryText)}>
                "{productName}"
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Main Category Selection */}
          <div className="mb-4">
            <label className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
              Main Category
            </label>
            <div className={cn(
              "flex gap-2 border rounded-xl p-2",
              borderPrimary
            )}>
              {MAIN_CATEGORIES.map((mainCat) => (
                <button
                  key={mainCat.value}
                  onClick={() => handleMainCategoryChange(mainCat.value)}
                  className={cn(
                    "flex-1 p-3 text-center hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark transition-colors rounded-lg flex items-center justify-center gap-2",
                    borderPrimary,
                    selectedMainCategory === mainCat.value && "bg-emerald-50 dark:bg-darkGreenColor-dark/20 border-2"
                  )}
                >
                  <span className={cn(mediumText, baseTextSize, primaryText)}>
                    {getMainCategoryDisplayName(mainCat.value)}
                  </span>
                  {selectedMainCategory === mainCat.value && (
                    <Check className="w-5 h-5 text-emerald-600 dark:text-textGreen-dark" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Category Selection (only shown if mainCategory is selected) */}
          {selectedMainCategory && (
            <div className="mb-4">
              <label className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
                Category
              </label>
              <div className={cn(
                "max-h-[200px] overflow-y-auto border rounded-xl pr-2",
                borderPrimary
              )}>
                {availableCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    className={cn(
                      "w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark transition-colors border-b last:border-b-0 flex items-center justify-between",
                      borderPrimary,
                      selectedCategory === category && "bg-emerald-50 dark:bg-darkGreenColor-dark/20"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getCategoryEmoji(category)}</span>
                      <span className={cn(mediumText, baseTextSize, primaryText)}>
                        {getCategoryDisplayName(category, selectedMainCategory)}
                      </span>
                    </div>
                    {selectedCategory === category && (
                      <Check className="w-5 h-5 text-emerald-600 dark:text-textGreen-dark" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Subcategory Selection (only shown if category is selected and has subcategories) */}
          {selectedCategory && availableSubCategories.length > 0 && (
            <div className="mb-4 flex-1 overflow-hidden flex flex-col">
              <label className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
                Subcategories (optional)
              </label>
              <div className={cn(
                "flex-1 overflow-y-auto border rounded-xl p-2",
                borderPrimary
              )}>
                <div className="space-y-2">
                  {availableSubCategories.map((subCategory) => {
                    const isSelected = selectedSubCategories.includes(subCategory);
                    const subCategoryStyles = getSubCategoryStyles(subCategory, darkMode);
                    return (
                      <label
                        key={subCategory}
                        className={cn(
                          "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark transition-colors",
                          isSelected && "bg-emerald-50 dark:bg-darkGreenColor-dark/20"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSubCategoryToggle(subCategory)}
                          className="rounded border-border dark:border-border-dark"
                        />
                        <span
                          className={cn(mediumText, baseTextSize, "px-2 py-1 rounded-lg")}
                          style={subCategoryStyles}
                        >
                          {getSubCategoryDisplayName(subCategory)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="-mx-4 mt-4">
            <div className="border-t border-borderPrimary dark:border-borderPrimary-dark w-full" />
            <div className="flex justify-end gap-3 pt-4 px-4">
              <button
                onClick={onClose}
                className={cn(
                  smallTextSize,
                  secondaryText,
                  semiBoldText,
                  "rounded-xl px-4 py-3",
                  whiteBG,
                  "border",
                  borderPrimary,
                  "hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark",
                  "flex-1"
                )}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!selectedMainCategory || !selectedCategory || isSubmitting}
                className={cn(
                  smallTextSize,
                  "text-white dark:text-white",
                  semiBoldText,
                  "bg-darkGreenColor dark:bg-darkGreenColor-dark rounded-xl px-4 py-3 border",
                  borderPrimary,
                  "hover:bg-darkGreenColor/95",
                  "flex-1",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isSubmitting ? 'Updating...' : 'Update Category'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCategoryModal;
